import API from 'api';
import { ApiError, AuthRefreshResponse } from 'api/alectryon/schema';
import { MerchantDto } from 'api/generated';
import { ALECTRYON_API_HOST } from 'config';
import {
  LogoutReasons,
  login,
  logout,
  setStoreId,
  setTGUser,
} from 'reducers/userSlice';
import { store } from 'store';

export type TGUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
};

export enum AuthStatuses {
  MERCHANT_NOT_FOUND = 'MERCHANT_NOT_FOUND',
  SUCCESS = 'SUCCESS',
  ACCESS_DENIED = 'ACCESS_DENIED',
  GENERIC_ERROR = 'GENERIC_ERROR',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
}

export type Auth =
  | {
      status:
        | AuthStatuses.GENERIC_ERROR
        | AuthStatuses.MERCHANT_NOT_FOUND
        | AuthStatuses.ACCESS_DENIED
        | AuthStatuses.UNAUTHENTICATED;
    }
  | {
      status: AuthStatuses.SUCCESS;
      merchant: MerchantDto;
    };

export function signIn(): void {
  window.location.href = alectryonUrl(
    '/alectryon/public-api/wpay/login?provider_name=telegram',
  );
}

export function signOut(): void {
  store.dispatch(logout());
  window.location.href = alectryonUrl('/alectryon/public-api/wpay/logout');
}

const AUTH_ERROR_CODE = {
  credsExpired: 'creds_expired',
  unauthorized: 'UNAUTHORIZED',
} as const;

class AuthRefreshError extends Error {
  constructor(readonly status: number, readonly body: ApiError | null) {
    super(`authRefresh failed (${status})`);
    this.name = 'AuthRefreshError';
  }
}

export const auth: () => Promise<Auth> = async () => {
  try {
    const data = await authRefresh();

    const userData: TGUser = {
      id: Number(data.user_info.telegram_id),
      first_name: data.user_info.first_name,
      last_name: data.user_info.last_name ?? undefined,
      username: data.user_info.telegram_username ?? undefined,
      photo_url: data.user_info.photo_url ?? undefined,
    };
    store.dispatch(setTGUser(userData));
    store.dispatch(login(data.jwt));

    const merchant = await API.Merchant.findMerchantByUser();

    switch (merchant.data.status) {
      case 'ACCESS_DENIED':
        store.dispatch(logout());
        return { status: AuthStatuses.ACCESS_DENIED };
      case 'NOT_FOUND':
        return { status: AuthStatuses.MERCHANT_NOT_FOUND };
      case 'SUCCESS': {
        const merchantData = merchant.data.data as MerchantDto;
        const storeId =
          merchantData.stores.length > 0 ? merchantData.stores[0].id : null;
        store.dispatch(setStoreId(storeId));
        return { status: AuthStatuses.SUCCESS, merchant: merchantData };
      }
    }
  } catch (e) {
    const code = e instanceof AuthRefreshError ? e.body?.code : undefined;
    if (code === AUTH_ERROR_CODE.unauthorized) {
      store.dispatch(logout());
      return { status: AuthStatuses.UNAUTHENTICATED };
    }
    console.error('[auth] failed:', e);
    const reason =
      code === AUTH_ERROR_CODE.credsExpired ? LogoutReasons.expired : undefined;
    store.dispatch(logout(reason));
    return { status: AuthStatuses.GENERIC_ERROR };
  }
};

export function alectryonUrl(path: string): string {
  return ALECTRYON_API_HOST ? `${ALECTRYON_API_HOST}${path}` : path;
}

export async function authRefresh(): Promise<AuthRefreshResponse> {
  const response = await fetch(
    alectryonUrl('/alectryon/public-api/wpay/auth-refresh'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      credentials: 'include',
    },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiError | null;
    throw new AuthRefreshError(response.status, body);
  }
  return response.json() as Promise<AuthRefreshResponse>;
}
